/**
 * Game Time module
 *
 * Manages the in-game time for content visibility filtering.
 * Supports volume-specific base dates with wall-clock time.
 *
 * Volume 1: November 20, 2042 (Employee desktops - amber theme)
 * Volume 2: December 5, 2042 (Network/Intranet - purple theme)
 *
 * Only the time of day comes from the player's wall-clock (converted to PT).
 *
 * Game time is a `Temporal.ZonedDateTime` pinned to Pacific Time. Modelling it
 * as a zoned instant — rather than a `Date` whose local components secretly
 * hold PT values — is what keeps formatting honest: every formatter reads the
 * zone off the value itself, so there is no second conversion to get wrong.
 *
 * Requires native `Temporal` (Chrome 137+, Firefox 139+, Safari 26+). Tests
 * install a polyfill; see vitest.setup.temporal.ts for why.
 *
 * @module time
 */

import { createSignal } from "solid-js";

/** Game volume type */
export type GameVolume = 1 | 2;

/** Options for game time functions */
export interface GameTimeOptions {
  /** Game volume (defaults to 1) */
  volume?: GameVolume;
}

/** Game is set in Pacific Time */
export const GAME_TIMEZONE = "America/Los_Angeles";

/** Fixed game date - Volume 1: November 20, 2042 */
export const GAME_YEAR = 2042;
export const GAME_MONTH = 11; // November (1-indexed for clarity)
export const GAME_DAY = 20;

/** Fixed game date - Volume 2: December 5, 2042 */
export const GAME_YEAR_V2 = 2042;
export const GAME_MONTH_V2 = 12; // December (1-indexed for clarity)
export const GAME_DAY_V2 = 5;

/**
 * Get the base date for a game volume.
 *
 * @param {GameVolume} volume - The game volume (1 or 2)
 * @returns {Temporal.PlainDate} Base date for the volume
 */
export function getVolumeBaseDate(volume: GameVolume = 1): Temporal.PlainDate {
  if (volume === 2) {
    return Temporal.PlainDate.from({
      year: GAME_YEAR_V2,
      month: GAME_MONTH_V2,
      day: GAME_DAY_V2,
    });
  }
  return Temporal.PlainDate.from({
    year: GAME_YEAR,
    month: GAME_MONTH,
    day: GAME_DAY,
  });
}

/**
 * Get current game time: the volume's fixed date at the player's current
 * time of day, expressed in Pacific Time.
 *
 * E.g. a player in GMT at 18:00 gets Nov 20, 2042 at 10:00 PT (Volume 1).
 *
 * @param {GameTimeOptions} [options] - Options including volume selection
 * @returns {Temporal.ZonedDateTime} Current game time in Pacific Time
 */
export function getCurrentGameTime(
  options?: GameTimeOptions
): Temporal.ZonedDateTime {
  const nowInPacific = Temporal.Now.zonedDateTimeISO(GAME_TIMEZONE);
  return getVolumeBaseDate(options?.volume).toZonedDateTime({
    timeZone: GAME_TIMEZONE,
    plainTime: nowInPacific.toPlainTime(),
  });
}

// Reactive state for Volume 1
const [gameTime, setGameTimeInternal] = createSignal(getCurrentGameTime());

// Reactive state for Volume 2
const [gameTimeV2, setGameTimeV2Internal] = createSignal(
  getCurrentGameTime({ volume: 2 })
);

/**
 * Set the game time for a specific volume.
 *
 * @param {Temporal.ZonedDateTime} date - The time to set
 * @param {GameTimeOptions} [options] - Options including volume selection
 */
export function setGameTime(
  date: Temporal.ZonedDateTime,
  options?: GameTimeOptions
): void {
  if (options?.volume === 2) {
    setGameTimeV2Internal(date);
  } else {
    setGameTimeInternal(date);
  }
}

/**
 * Set the game time for Volume 2.
 *
 * @param {Temporal.ZonedDateTime} date - The time to set
 */
export function setGameTimeV2(date: Temporal.ZonedDateTime): void {
  setGameTimeV2Internal(date);
}

export { gameTime, gameTimeV2 };

/**
 * Read the current game time signal for a volume.
 *
 * @param {GameTimeOptions} [options] - Options including volume selection
 * @returns {Temporal.ZonedDateTime} The volume's current game time
 */
function currentFor(options?: GameTimeOptions): Temporal.ZonedDateTime {
  return options?.volume === 2 ? gameTimeV2() : gameTime();
}

// Shared ticking clock.
//
// `getCurrentGameTime` only samples the wall clock once, so without this the
// game time is frozen at module-init forever. One interval drives both volumes
// and is reference-counted, so any number of mounted panels share a single
// timer. Ticking by elapsed real time (rather than resampling the wall clock)
// keeps a player-chosen date from snapping back on the next tick, and stays
// accurate across suspended tabs.
let clockSubscribers = 0;
let clockIntervalId: ReturnType<typeof setInterval> | undefined;
let lastTickAt: Temporal.Instant | undefined;

/**
 * Start the shared game clock, advancing both volume signals once a second.
 *
 * @returns {() => void} Unsubscribe function; the timer stops when the last
 *   subscriber releases it.
 */
export function startGameClock(): () => void {
  clockSubscribers++;

  if (clockIntervalId === undefined) {
    lastTickAt = Temporal.Now.instant();
    clockIntervalId = setInterval(() => {
      const now = Temporal.Now.instant();
      const elapsed = now.since(lastTickAt ?? now);
      lastTickAt = now;
      setGameTimeInternal((prev) => prev.add(elapsed));
      setGameTimeV2Internal((prev) => prev.add(elapsed));
    }, 1000);
  }

  let released = false;
  return () => {
    if (released) return;
    released = true;
    clockSubscribers--;
    if (clockSubscribers === 0 && clockIntervalId !== undefined) {
      clearInterval(clockIntervalId);
      clockIntervalId = undefined;
      lastTickAt = undefined;
    }
  };
}

/**
 * Parse a lore timestamp into a Pacific-Time instant.
 *
 * Lore timestamps are written without an offset (e.g. "2042-11-20T09:00:00"),
 * which means "09:00 in the office", so a bare timestamp is interpreted in the
 * game timezone rather than the player's. Timestamps that do carry an offset
 * or a Z suffix are respected as written.
 *
 * Some email content still uses prose timestamps ("2 days ago", "Yesterday at
 * 11:47 PM"). Those have no absolute meaning, so they return null and callers
 * treat them as already-delivered — which is what happened before, when
 * `new Date("2 days ago")` produced an Invalid Date that compared false in
 * every direction. Returning null keeps that outcome without pretending a
 * comparison happened.
 *
 * @param {string} isoTimestamp - ISO 8601 timestamp string
 * @returns {Temporal.ZonedDateTime | null} The timestamp, or null if it is not
 *   an absolute time
 */
export function parseLoreTimestamp(
  isoTimestamp: string
): Temporal.ZonedDateTime | null {
  try {
    if (/(?:Z|[+-]\d{2}:?\d{2})$/.test(isoTimestamp)) {
      return Temporal.Instant.from(isoTimestamp).toZonedDateTimeISO(
        GAME_TIMEZONE
      );
    }
    return Temporal.PlainDateTime.from(isoTimestamp).toZonedDateTime(
      GAME_TIMEZONE
    );
  } catch {
    return null;
  }
}

/**
 * Check if a timestamp is after the current game time.
 *
 * @param {string} isoTimestamp - ISO 8601 timestamp string
 * @param {GameTimeOptions} [options] - Options including volume selection
 * @returns {boolean} True if the timestamp is in the future
 */
export function isAfterGameTime(
  isoTimestamp: string,
  options?: GameTimeOptions
): boolean {
  const parsed = parseLoreTimestamp(isoTimestamp);
  if (parsed === null) return false;
  return Temporal.ZonedDateTime.compare(parsed, currentFor(options)) > 0;
}

/**
 * Check if a timestamp is before the current game time.
 *
 * @param {string} isoTimestamp - ISO 8601 timestamp string
 * @param {GameTimeOptions} [options] - Options including volume selection
 * @returns {boolean} True if the timestamp is in the past
 */
export function isBeforeGameTime(
  isoTimestamp: string,
  options?: GameTimeOptions
): boolean {
  const parsed = parseLoreTimestamp(isoTimestamp);
  if (parsed === null) return false;
  return Temporal.ZonedDateTime.compare(parsed, currentFor(options)) < 0;
}

/**
 * Get the game date as a long-form string, e.g. "November 20, 2042".
 *
 * @param {GameTimeOptions} [options] - Options including volume selection
 * @returns {string} Formatted date string
 */
export function getGameDate(options?: GameTimeOptions): string {
  // Formatted from the PlainDate, never the ZonedDateTime: a plain date has no
  // zone for Intl to "helpfully" convert into the host's, which is how the
  // displayed day used to drift near midnight.
  return currentFor(options).toPlainDate().toLocaleString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/**
 * Get the game date formatted for terminal chrome, e.g. "NOV 20, 2042".
 *
 * @param {GameTimeOptions} [options] - Options including volume selection
 * @returns {string} Uppercased short-form date string
 */
export function getGameDateTerminal(options?: GameTimeOptions): string {
  return currentFor(options)
    .toPlainDate()
    .toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    })
    .toUpperCase();
}

/**
 * Get the game date and time as "YYYY.MM.DD HH:MM".
 *
 * @param {GameTimeOptions} [options] - Options including volume selection
 * @returns {string} Formatted date-time string
 */
export function getGameDateTime(options?: GameTimeOptions): string {
  const t = currentFor(options);
  const pad = (n: number, width = 2) => String(n).padStart(width, "0");
  return `${t.year}.${pad(t.month)}.${pad(t.day)} ${pad(t.hour)}:${pad(t.minute)}`;
}

/**
 * Get the game clock as "HH:MM:SS".
 *
 * @param {GameTimeOptions} [options] - Options including volume selection
 * @returns {string} Zero-padded 24-hour clock string
 */
export function getGameClock(options?: GameTimeOptions): string {
  const t = currentFor(options);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(t.hour)}:${pad(t.minute)}:${pad(t.second)}`;
}
